import axios from 'axios';

/**
 * Composable for tracking page views with UTM campaign attribution.
 * Only tracks when UTM parameters are present (campaign visitors).
 * Uses session-based deduplication to avoid counting refreshes.
 */
export function usePageViewTracking() {
    /**
     * Generate an RFC4122 v4 UUID. Prefers crypto.randomUUID() (secure
     * contexts on modern browsers) and falls back to a Math.random-based
     * implementation when it's unavailable — e.g., HTTP origins, Safari < 15.4,
     * embedded webviews. The fallback is not cryptographically strong but
     * collision risk for a per-session id is negligible.
     */
    const generateUuid = () => {
        if (typeof crypto !== 'undefined') {
            if (typeof crypto.randomUUID === 'function') {
                return crypto.randomUUID();
            }
            if (typeof crypto.getRandomValues === 'function') {
                const bytes = new Uint8Array(16);
                crypto.getRandomValues(bytes);
                bytes[6] = (bytes[6] & 0x0f) | 0x40;
                bytes[8] = (bytes[8] & 0x3f) | 0x80;
                const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
                return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
            }
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    };

    /**
     * Get or create a session ID for deduplication.
     * Session ID is stored in sessionStorage and expires when browser tab closes.
     */
    const getOrCreateSessionId = () => {
        let sessionId = sessionStorage.getItem('campaign_session_id');
        if (!sessionId) {
            sessionId = generateUuid();
            sessionStorage.setItem('campaign_session_id', sessionId);
        }
        return sessionId;
    };

    /**
     * Track a page view with UTM parameters.
     * Only sends tracking request if UTM params are present.
     *
     * @param {string} pageType - 'landing' or 'form'
     * @param {string|null} documentNumber - Optional document number for company-specific tracking
     */
    const trackPageView = async (pageType, documentNumber = null) => {
        const urlParams = new URLSearchParams(window.location.search);

        // Track campaign visitors (UTM tags) AND experiment participants.
        // useExperiment writes client-side A/B buckets into the `experiments`
        // param, so a visitor can be bucketed without any UTM tags (direct or
        // organic traffic). Skip only when there's nothing to attribute on
        // either axis — otherwise the experiment's sessions never reach
        // campaign_page_view_experiments and its conversion funnel reads empty.
        const hasUtm = urlParams.has('utm_source') || urlParams.has('utm_campaign');
        const hasExperiments = (urlParams.get('experiments') || '').trim() !== '';
        if (!hasUtm && !hasExperiments) {
            return;
        }

        try {
            await axios.post('/api/track-page-view', {
                page_type: pageType,
                document_number: documentNumber,
                utm_source: urlParams.get('utm_source'),
                utm_medium: urlParams.get('utm_medium'),
                utm_campaign: urlParams.get('utm_campaign'),
                utm_content: urlParams.get('utm_content'),
                experiments: urlParams.get('experiments'),
                session_id: getOrCreateSessionId(),
            });
        } catch (e) {
            // Fail silently - tracking shouldn't break the page
            console.debug('Page view tracking failed:', e);
        }
    };

    /**
     * Build a path that preserves the current page's UTM/experiment params,
     * so analytics can follow a session through the landing → form → order
     * funnel without losing variant attribution.
     */
    const withCampaignParams = (path) => {
        const current = new URLSearchParams(window.location.search);
        const carry = new URLSearchParams();
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'experiments'].forEach((key) => {
            const value = current.get(key);
            if (value) {
                carry.set(key, value);
            }
        });

        const query = carry.toString();
        if (!query) {
            return path;
        }

        const separator = path.includes('?') ? '&' : '?';
        return `${path}${separator}${query}`;
    };

    return { trackPageView, getOrCreateSessionId, withCampaignParams };
}
